import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, confirmNewPassword } from '../api/auth';
import { friendlyAuthError } from '../utils/authErrors';
import '../styles/auth.css';

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setStep('confirm');
    } catch (err: unknown) {
      setError(friendlyAuthError(err, "Couldn't send the reset code — please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await confirmNewPassword(email, code, newPassword);
      navigate('/login', { state: { message: 'Password reset. You can now sign in.' } });
    } catch (err: unknown) {
      setError(friendlyAuthError(err, "Couldn't reset your password — please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Reset password</h1>
          <p className="auth-subtitle">
            {step === 'request'
              ? "Enter your email and we'll send a verification code"
              : 'Enter the code from your email and your new password'}
          </p>
        </div>

        {step === 'request' ? (
          <form onSubmit={handleRequestCode} className="auth-form">
            {error && <div className="auth-error">{error}</div>}

            <div className="form-field">
              <label className="field-label">Email</label>
              <input
                type="email"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="auth-form">
            {error && <div className="auth-error">{error}</div>}

            <div className="form-field">
              <label className="field-label">Verification Code</label>
              <input
                type="text"
                className="field-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                required
                autoFocus
              />
            </div>

            <div className="form-field">
              <label className="field-label">New Password</label>
              <input
                type="password"
                className="field-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">Confirm Password</label>
              <input
                type="password"
                className="field-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}

        <p className="auth-footer">
          <Link to="/login" className="auth-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
